// ─────────────────────────────────────────────────────────────────────────────
// SKELETON OVERLAY — Phase 1 Milestone completion
// Renders the 33-joint MediaPipe skeleton on a 2D canvas overlay
// Useful for debug, AR feedback, and the Phase 1 milestone demo
// ─────────────────────────────────────────────────────────────────────────────
import type { NormalizedLandmark } from "./pose";

// MediaPipe Pose connections (pairs of landmark indices)
// https://developers.google.com/mediapipe/solutions/vision/pose_landmarker
const POSE_CONNECTIONS: [number, number][] = [
  // Face
  [0, 1], [1, 2], [2, 3], [3, 7],
  [0, 4], [4, 5], [5, 6], [6, 8],
  // Torso
  [11, 12], [11, 23], [12, 24], [23, 24],
  // Arms
  [11, 13], [13, 15], [12, 14], [14, 16],
  [15, 17], [15, 19], [15, 21],
  [16, 18], [16, 20], [16, 22],
  [17, 19], [18, 20],
  // Legs
  [23, 25], [24, 26], [25, 27], [26, 28],
  [27, 29], [28, 30], [29, 31], [30, 32],
  [27, 31], [28, 32],
];

// Color groups for body segments
const JOINT_COLORS: Record<string, string> = {
  face:    "rgba(250, 204, 21,  0.9)",   // yellow
  torso:   "rgba(139, 92,  246, 0.9)",   // purple (sxt-primary)
  arms:    "rgba(34,  197, 94,  0.9)",   // green
  legs:    "rgba(59,  130, 246, 0.9)",   // blue
};

function getJointColor(idx: number): string {
  if (idx <= 10)              return JOINT_COLORS.face;
  if (idx >= 11 && idx <= 22) return idx <= 16 ? JOINT_COLORS.arms : JOINT_COLORS.arms;
  return JOINT_COLORS.legs;
}

function getConnectionColor(a: number, b: number): string {
  if (a <= 10 && b <= 10)     return JOINT_COLORS.face;
  if (a === 11 || a === 12 || b === 11 || b === 12 ||
      a === 23 || a === 24 || b === 23 || b === 24)
    return JOINT_COLORS.torso;
  if ((a >= 11 && a <= 22) || (b >= 11 && b <= 22)) return JOINT_COLORS.arms;
  return JOINT_COLORS.legs;
}

export interface SkeletonOverlayOptions {
  canvas: HTMLCanvasElement;
  /** Mirror horizontally (front camera) */
  mirror?: boolean;
  /** Show joint dots */
  showJoints?: boolean;
  /** Show bone connections */
  showBones?: boolean;
  /** Minimum visibility threshold 0–1 */
  minVisibility?: number;
  /** Joint radius in px */
  jointRadius?: number;
  /** Bone line width in px */
  lineWidth?: number;
}

export class SkeletonOverlay {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private opts: Required<SkeletonOverlayOptions>;

  constructor(opts: SkeletonOverlayOptions) {
    this.canvas = opts.canvas;
    this.ctx = opts.canvas.getContext("2d")!;
    this.opts = {
      mirror: opts.mirror ?? true,
      showJoints: opts.showJoints ?? true,
      showBones: opts.showBones ?? true,
      minVisibility: opts.minVisibility ?? 0.4,
      jointRadius: opts.jointRadius ?? 5,
      lineWidth: opts.lineWidth ?? 2,
      canvas: opts.canvas,
    };
  }

  draw(landmarks: NormalizedLandmark[], videoW: number, videoH: number) {
    const { ctx, opts } = this;
    const { mirror, minVisibility, showBones, showJoints, jointRadius, lineWidth } = opts;

    // Resize overlay canvas to match video
    if (this.canvas.width !== videoW) this.canvas.width = videoW;
    if (this.canvas.height !== videoH) this.canvas.height = videoH;

    ctx.clearRect(0, 0, videoW, videoH);

    // Helper: convert normalized to pixel (with optional mirror)
    const toPixel = (lm: NormalizedLandmark) => ({
      x: (mirror ? 1 - lm.x : lm.x) * videoW,
      y: lm.y * videoH,
      vis: lm.visibility ?? 1,
    });

    if (showBones) {
      ctx.lineWidth = lineWidth;
      for (const [a, b] of POSE_CONNECTIONS) {
        const pa = landmarks[a];
        const pb = landmarks[b];
        if (!pa || !pb) continue;
        if ((pa.visibility ?? 1) < minVisibility || (pb.visibility ?? 1) < minVisibility) continue;

        const pa2 = toPixel(pa);
        const pb2 = toPixel(pb);
        const color = getConnectionColor(a, b);

        // Draw glow
        ctx.strokeStyle = color.replace("0.9", "0.25");
        ctx.lineWidth = lineWidth * 3;
        ctx.beginPath();
        ctx.moveTo(pa2.x, pa2.y);
        ctx.lineTo(pb2.x, pb2.y);
        ctx.stroke();

        // Draw bone
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;
        ctx.beginPath();
        ctx.moveTo(pa2.x, pa2.y);
        ctx.lineTo(pb2.x, pb2.y);
        ctx.stroke();
      }
    }

    if (showJoints) {
      for (let i = 0; i < landmarks.length; i++) {
        const lm = landmarks[i];
        if ((lm.visibility ?? 1) < minVisibility) continue;

        const { x, y, vis } = toPixel(lm);
        const color = getJointColor(i);
        const r = jointRadius * (0.7 + 0.3 * vis);

        // Glow halo
        ctx.beginPath();
        ctx.arc(x, y, r * 2.5, 0, Math.PI * 2);
        ctx.fillStyle = color.replace("0.9", "0.15");
        ctx.fill();

        // Joint dot
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();

        // White core
        ctx.beginPath();
        ctx.arc(x, y, r * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,255,255,0.9)";
        ctx.fill();
      }
    }
  }

  clear() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  setOptions(opts: Partial<SkeletonOverlayOptions>) {
    Object.assign(this.opts, opts);
  }
}
